import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import './lngvty-cart-bundles.js?v=parent-bundles-20261004';

class LngvtyCartUpsells extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-lngvty-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const rect = button.getBoundingClientRect();
    button.style.setProperty('--lngvty-add-x', `${event.clientX ? event.clientX - rect.left : rect.width / 2}px`);
    button.style.setProperty('--lngvty-add-y', `${event.clientY ? event.clientY - rect.top : rect.height / 2}px`);
    this.#addProduct(button);
  };

  async #addProduct(button) {
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const label = button.querySelector('span');
    const originalLabel = label?.textContent || 'Add';
    const status = this.querySelector('[role="status"]');

    if (!variantId) return;

    button.disabled = true;
    button.classList.remove('is-added');
    button.classList.add('is-adding');
    if (label) label.textContent = 'Adding…';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', [...new Set(sectionIds)].join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const config = fetchConfig('javascript', { body: formData });
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw Object.assign(new Error(result.description || result.message || 'Unable to add this item.'), { result });
      }

      button.classList.remove('is-adding');
      button.classList.add('is-added');
      if (label) label.textContent = `✓ ${this.dataset.addedLabel || 'Added'}`;
      if (status) status.textContent = this.dataset.addedLabel || 'Added to cart';

      void this.#flyToCart(button).catch(() => {});

      this.dispatchEvent(
        new CartAddEvent(result, `lngvty-upsell-${variantId}`, {
          source: 'lngvty-cart-upsells',
          itemCount: 1,
          productId,
          variantId,
          sections: result.sections,
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add this item.';
      button.classList.remove('is-adding', 'is-added');
      if (label) label.textContent = 'Try again';
      if (status) status.textContent = message;

      this.dispatchEvent(
        new CartErrorEvent(
          `lngvty-upsell-${variantId}`,
          message,
          error?.result?.description || message,
          error?.result?.errors || {}
        )
      );

      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
      }, 1300);
    } finally {
      button.disabled = false;
    }
  }

  async #flyToCart(button) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const card = button.closest('.lngvty-cart-upsells__card');
    const image = card?.querySelector('.lngvty-cart-upsells__media img');
    const target = document.querySelector('#cart-drawer-header .cart-drawer__heading small');
    if (!(image instanceof HTMLImageElement) || !(target instanceof HTMLElement)) return;

    const sourceRect = image.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const size = Math.min(Math.max(sourceRect.width * 0.58, 54), 84);
    const startX = sourceRect.left + (sourceRect.width - size) / 2;
    const startY = sourceRect.top + (sourceRect.height - size) / 2;
    const endX = targetRect.left + targetRect.width / 2 - size / 2;
    const endY = targetRect.top + targetRect.height / 2 - size / 2;
    const deltaX = endX - startX;
    const deltaY = endY - startY;

    const ghost = image.cloneNode();
    ghost.className = 'lngvty-cart-fly-ghost';
    ghost.removeAttribute('loading');
    Object.assign(ghost.style, {
      left: `${startX}px`,
      top: `${startY}px`,
      width: `${size}px`,
      height: `${size}px`,
    });
    document.body.append(ghost);

    const flight = ghost.animate(
      [
        { opacity: 0.96, transform: 'translate3d(0, 0, 0) rotate(0deg) scale(1)' },
        {
          opacity: 1,
          transform: `translate3d(${deltaX * 0.34}px, ${deltaY * 0.18 - 76}px, 0) rotate(8deg) scale(0.9)`,
          offset: 0.36,
        },
        {
          opacity: 0.94,
          transform: `translate3d(${deltaX * 0.74}px, ${deltaY * 0.56 - 58}px, 0) rotate(-5deg) scale(0.66)`,
          offset: 0.72,
        },
        { opacity: 0, transform: `translate3d(${deltaX}px, ${deltaY}px, 0) rotate(0deg) scale(0.2)` },
      ],
      { duration: 460, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
    );

    await flight.finished.catch(() => {});
    ghost.remove();
    target.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.28)', offset: 0.45 },
        { transform: 'scale(1)' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
    );
  }
}

if (!customElements.get('lngvty-cart-upsells')) {
  customElements.define('lngvty-cart-upsells', LngvtyCartUpsells);
}
